import { SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import { EvaChartStates } from "types/factory";
import { isLessThanSampleSizeLabel } from "utils/groupBy/isLessThanSampleSizeLabel";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { createChartLocks } from "../locks";
import { DEFAULT_Z_INDEX, HIGHLIGHTED_Z_INDEX, SELECTED_WELL_Z_INDEX } from "./constants";
import { dropOthersFromStackedBar } from "./hideOthers";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  computeSelectionStates,
  formatChartTitleWithSubscript,
  getStackedBarChartMinMax,
  getStackedBarPositions
} from "./utils";

export function translateStackedBarResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    typeWellSeries = [],
    hoverLegendItem,
    hoverLegendGroup,
    attentionWells,
    chartType,
    xAxisFieldSync,
    xAxisSort,
    focusLegendItems,
    facilityFocusLegendItems,
    chartTypeParam,
    groupBy,
    splitYearApplied,
    hideOthers,
    histogramToggle
  } = states;

  // EVA-306: drop Others first, so min/max and the position-total sort both
  // recompute from the remaining bars.
  let { layout, series } = states;
  if (hideOthers?.active) {
    ({ series, layout } = dropOthersFromStackedBar(series, layout));
  }

  const yAxisTitle = layout?.yAxis?.title || "";
  const { yMin, yMax } = getStackedBarChartMinMax(series);

  const { lockedYMax, lockedYMin } = createChartLocks({
    type: EvaChart.StackedBar,
    states
  });

  let options = createEntityState(ChartOption.Chart, {
    chartType,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        name: formatChartTitleWithSubscript(yAxisTitle),
        nameGap: 43,
        nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
        min: lockedYMin,
        max: lockedYMax,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: Math.floor(yMin.value),
            type: yMin.type
          },
          max: {
            value: Math.floor(yMax.value),
            type: yMax.type,
            override: true // it manages if lock should be overridden or not
          }
        },
        axisPointer: {
          label: {
            margin: SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
          }
        }
      })
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxisCategory, {
        name: formatChartTitleWithSubscript(layout.xAxis.title),
        data: layout.xAxis.data,
        nameGap: 27,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false, // useY is managed at hd chart,
        silent: true
      })
    },
    grid: { top: 46, bottom: 46, left: 62, right: 20 },
    series: series.map((location, index) => {
      const { label, style, y, groupTitle } = location;

      const data = y;
      const id =
        groupTitle !== label
          ? `${groupTitle}, ${label}, ${index}`
          : `${groupTitle}, ${index}`;
      const { isSelectedWell, isHovered } = computeSelectionStates({
        hoverLegendItem,
        hoverLegendGroup,
        attentionWells,
        label,
        groupTitle
      });
      const barSeries = createEntityState(ChartOption.Bar, {
        id,
        name: label,
        data: data,
        itemStyle: { color: style?.hexColor ?? "#000" },
        stack: "stackedBar",
        z: isSelectedWell
          ? SELECTED_WELL_Z_INDEX
          : isHovered
          ? HIGHLIGHTED_Z_INDEX
          : DEFAULT_Z_INDEX
      });

      return barSeries;
    })
  });

  const sortedSeries = Array.isArray(options.series) ? [...options.series] : [];
  // EVA-127: compared on `.title` before — the raw column title, which is
  // display text (see CONTEXT.md's warning against matching on it). A custom
  // field's group-by used to carry the qualified, unit-suffixed title, so this
  // never matched and axis sync stayed off; now the group-by and x-axis titles
  // are equal strings and sync silently turns on. `.property` is the stable
  // identifier and what "same field" actually means, so compare on that
  // instead — it also fixes the stale-persisted-param case, where a re-picked
  // group-by is bare but a chart param saved pre-EVA-127 still carries the old
  // qualified title.
  const isSameGroupBy =
    !!groupBy?.property && groupBy.property === chartTypeParam?.properties?.x?.property;

  const legendItems =
    chartType === EvaChart.StackedBarMidstream
      ? facilityFocusLegendItems
      : focusLegendItems;

  const isAxisSync =
    !splitYearApplied &&
    xAxisFieldSync?.active &&
    legendItems &&
    legendItems.length > 0 &&
    isSameGroupBy;

  let xAxisData: string[];
  let indexMapping: number[];

  if (isAxisSync) {
    // Use axis sync ordering with filtering
    // EVA-306: this rebuilds the x-axis from the focus legend, not from
    // layout.xAxis.data — dropOthersFromStackedBar above never touches this list,
    // so Others has to be filtered out here too or it reappears on a synced axis.
    const filteredLegendItems = legendItems.filter(
      (item) =>
        item.inFilter !== false &&
        !(
          hideOthers?.active &&
          (isOthersGroupLabel(item.title) || isLessThanSampleSizeLabel(item.title))
        )
    );
    const legendOrder = filteredLegendItems.map((item) => item.title);

    xAxisData = legendOrder;
    indexMapping = legendOrder.map((newLabel) =>
      layout.xAxis.data.findIndex((originalLabel) => originalLabel === newLabel)
    );

    sortedSeries.sort((a, b) => {
      const aIndex = legendOrder.indexOf(a.name);
      const bIndex = legendOrder.indexOf(b.name);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
  } else {
    // The x-axis sort button stays live even when Split Year is applied — the years are
    // just another set of bucket labels here, and suppressing the user's explicit sort
    // choice made the sort control silently do nothing while still showing as active.
    const positions = getStackedBarPositions(
      layout.xAxis.data,
      sortedSeries,
      xAxisSort.properties?.value,
      // Histogram bins the focus field, so the category axis bin is not what ranks.
      histogramToggle?.active ? null : chartTypeParam?.properties?.x?.bin?.MaxBins,
      layout.xAxis.maxBinsSortValues
    );

    xAxisData = positions.map((item) => item.label);
    indexMapping = positions.map((item) => item.index);
  }

  // Apply data reordering to all series
  sortedSeries.forEach((series) => {
    if (Array.isArray(series.data)) {
      const originalData = [...series.data];
      series.data = indexMapping.map((originalIndex) =>
        originalIndex === -1 ? null : originalData[originalIndex]
      );
    }
  });

  options.xAxis = {
    ...createEntityState(ChartOption.XAxisCategory, {
      name: formatChartTitleWithSubscript(layout.xAxis.title),
      data: xAxisData,
      nameGap: 27,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      useY: false, // useY is managed at hd chart,
      silent: true
    })
  };

  options.legend = {
    ...((options.legend as object) || {}),
    data: sortedSeries.map((series) => ({
      name: series.name as string,
      itemStyle: {
        color: isAxisSync
          ? legendItems.find((item) => item.title === series.name)?.color
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (series as any).itemStyle?.color || "#000"
      }
    })),
    show: false
  };

  options.series = sortedSeries;

  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }
  if (series.length === 0 && typeWellSeries.length === 0) {
    // The Hide Others redraw uses replaceMerge "series", which keeps the old
    // category axis unless it is cleared explicitly; both axes are needed so a
    // notMerge rebuild of this shape doesn't throw.
    options =
      hideOthers?.active && states.series?.length > 0
        ? { xAxis: { type: "category", data: [] }, yAxis: { type: "value" }, series: [] }
        : {};
  }
  return options;
}
